import { DeleteOutlined } from '@ant-design/icons';
import { useMDSContext } from '@metservice/use-mds';
import { createAxiosCancelTokenSource } from '@metservicedev/platforms-services-interface';
import { movIntegrationMDS } from '@metservicedev/platforms-services-interface';
import { toHash } from '@mono/to-hash';
import { usePlatStageContext } from '@mono/use-plat-stage';
import { Button, Radio, Switch } from 'antd';
import { set, times } from 'lodash';
import { Duration } from 'luxon';
import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';

import CapabilitySelector from '../../../WidgetConfiguratorComponents/CapabilitySelector';
import { ColourPicker, Datability, Divider, Select, Slider, Text, TimeRange } from '../../../WidgetConfiguratorComponents/FormGroups';
import { THEME_TIME_RANGE } from '../../../WidgetConfiguratorComponents/FormGroups/TimeRangeFormGroup';
import { capabilitiesByDataTypes } from '../../../utils/capabilitiesByDataTypes';
import { filterDirectionalCapabilities } from '../../../utils/filterDirectionalCapabilities';
import { generateMarks } from '../../../utils/generateMarks';
import { LineStyleFormGroup } from '../LineStyleFormGroup';
import smoothingOptions from '../smoothingOptions';
import symbolOptions from '../symbolOptions';
import { ExtremesConfigurator } from './ExtremesConfigurator';

const StyledRadio = styled.div`
    padding: 4px;
    text-align: center;
`;

const LineGraphItem = ({
    onDelete,
    onChange,
    item,
    datability,
    directionalDatability,
    onDatabilityChange,
    onDirectionalCapabilityChange,
    onTimeRangeChange,
}) => {
    const { capabilities } = useMDSContext();
    const { style, smoothing, symbol, colour, label, zOrder, unit, unitLabel, direction, enableEnsemble } = item;
    const [directionalCapabilities, setDirectionalCapabilities] = useState([]);
    const [passedDownDatasets, setPassedDownDatasets] = useState([]);
    const [loadingDirectionalCapabilities, setLoadingingDirectionalCapabilities] = useState(false);
    const mds = useMDSContext();
    const { stage } = usePlatStageContext();

    const accessToken = JSON.parse(localStorage.getItem('okta-token-storage') ?? '{}')?.accessToken?.accessToken;
    const headers = { 'X-Current-Org': localStorage.getItem('mip-organisation-id') };

    const onInterpolationChange = (interpolationInterval) => {
        onDatabilityChange(item, datability, {
            ...datability,
            interpolationInterval,
        });
    };
    const data_structure = ['timeseries/point2d' + (enableEnsemble ? '/collection' : '')];

    const getDatasetBody = {
        space: datability.space,
        data_structure,
        // has_vertical_dimension: true,
        mime_type: ['application/vnd.metocean.cf+json'],
        radius: typeof datability._radius === 'number' ? datability._radius : 5e3,
        includeFields: ['id', 'public_attributes', 'data_type', 'display_name_long', 'display_name_short', 'temporal_nature'],
    };

    useEffect(() => {
        if (capabilities.length === 0) return;
        if (!datability.space && !directionalDatability.space) return;

        (async () => {
            if (!datability.datasetId && !datability.capabilityId && !directionalDatability.datasetId && !directionalDatability.capabilityId) {
                console.log('no datasetId or capabilityId', datability);
                setDirectionalCapabilities(filterDirectionalCapabilities({ capabilities: capabilitiesByDataTypes(capabilities, [`scalar`]) }));
                setPassedDownDatasets([]);
                return;
            }

            const cancelTokenSource = createAxiosCancelTokenSource();

            if (datability.datasetId) {
                setPassedDownDatasets([]);
                setLoadingingDirectionalCapabilities(true);
                mds.findCapabilitiesByDataset(datability.datasetId)
                    .then(({ capabilities }) => {
                        return capabilities.map((c) => c.id);
                    })
                    .then((capabilityIds) => {
                        const validCapabilities = capabilities.filter(({ id }) => capabilityIds.includes(id));
                        const filteredCapabilities = filterDirectionalCapabilities({ capabilities: validCapabilities });
                        filteredCapabilities && setDirectionalCapabilities(filteredCapabilities);
                    })
                    .catch((e) => {
                        console.error(e);
                        setLoadingingDirectionalCapabilities(false);
                    })
                    .finally(setLoadingingDirectionalCapabilities(false));
            } else if (datability.capabilityId || directionalDatability.capabilityId) {
                setLoadingingDirectionalCapabilities(true);

                const [allDatasetsResponse, directionalDatasetsResponse] = await Promise.all(
                    [datability.capabilityId, directionalDatability.capabilityId].map((capabilityId) =>
                        movIntegrationMDS.http.getDatasets({
                            metjwt: accessToken,
                            stage,
                            headers,
                            data: {
                                ...getDatasetBody,
                                capabilityIds: [capabilityId],
                            },
                        })
                    )
                );

                const allDatasets = allDatasetsResponse.data;
                const directionalDatasets = directionalDatasetsResponse.data;

                const crossSectionDatasets =
                    allDatasets && allDatasets.length > 0
                        ? directionalDatasets && directionalDatasets.length > 0
                            ? allDatasets.filter((dataset) => directionalDatasets.find((directionalDataset) => dataset.id === directionalDataset.id))
                            : allDatasets
                        : directionalDatasets;

                setPassedDownDatasets(crossSectionDatasets);
                const datasetIds = crossSectionDatasets.map(({ id }) => id);
                movIntegrationMDS.http
                    .getCapabilitiesByDatasetIds({
                        datasetIds,
                        metjwt: accessToken,
                        stage,
                        headers,
                    })
                    .then((response) => {
                        const capabilityIds = response.data;
                        const validCapabilities = capabilities.filter(({ id }) => capabilityIds.includes(id));
                        const filteredCapabilities = filterDirectionalCapabilities({ capabilities: validCapabilities });
                        filteredCapabilities && setDirectionalCapabilities(filteredCapabilities);
                    })
                    .catch((e) => {
                        console.error(e);
                    })
                    .finally(setLoadingingDirectionalCapabilities(false));
            }
            return () => {
                cancelTokenSource.cancel();
            };
        })();
    }, [toHash(datability), toHash(directionalDatability), capabilities.length]);

    const compensatedTimeRange = useMemo(() => {
        const [early, late] = datability.timeRange.split('--').map((d) => Duration.fromISO(d));
        return [early.plus({ days: 1 }), late.minus({ days: 1 })].map((d) => d.toISO()).join('--');
    }, [datability.timeRange]);

    return (
        <>
            <Datability
                passedDownDatasets={passedDownDatasets}
                onChangeDatability={(newDatability) => onDatabilityChange(item, datability, newDatability)}
                unit={unit}
                unitLabel={unitLabel}
                onUnitLabelChange={(unitLabel) => onChange({ ...item, unitLabel })}
                onUnitChange={({ unit }) => onChange({ ...item, unit, unitLabel: unit })}
                dataType={[`scalar`]}
                datability={datability}
                disableInterpolation={false}
                onInterpolationChange={onInterpolationChange}
                dataStructures={data_structure}
            />
            <div style={{ gridColumn: 1 }}>
                <strong>Ensemble data:</strong>
            </div>
            <div style={{ gridColumn: '2/2' }}>
                <Switch
                    checked={enableEnsemble}
                    onChange={(enableEnsemble) => {
                        onChange({ ...item, enableEnsemble });
                        onDatabilityChange(item, datability, {
                            ...datability,
                            data_structure: enableEnsemble ? [`timeseries/point2d/collection`] : [`timeseries/point2d`],
                        });
                    }}
                />
            </div>
            <div style={{ height: 16 }} />
            <div style={{ gridColumn: 1 }}>
                <strong>Directional Capability:</strong>
            </div>
            <div style={{ gridColumn: '2/6' }}>
                <CapabilitySelector
                    loading={loadingDirectionalCapabilities}
                    value={directionalDatability?.capabilityId}
                    capabilities={directionalCapabilities}
                    onChange={(newCapability) => {
                        onDirectionalCapabilityChange(item, newCapability, directionalDatability, datability);
                    }}
                    dataType={[`scalar`]}
                />
            </div>
            {directionalDatability && directionalDatability.datasetId && directionalDatability.capabilityId && (
                <>
                    <Slider
                        label='Arrow Size:'
                        onChange={(arrowSize) => onChange({ ...item, arrowSize })}
                        value={item.arrowSize ?? 1}
                        min={0.1}
                        max={2}
                        step={0.1}
                        tipFormatter={(value) => `${value * 100}%`}
                    >
                        {' '}
                    </Slider>
                </>
            )}
            {directionalDatability && directionalDatability.datasetId && directionalDatability.capabilityId && (
                <div style={{ gridColumn: 1 }}>
                    <div style={{ fontWeight: 'bold' }}>Arrow interval:</div>
                </div>
            )}
            {directionalDatability && directionalDatability.datasetId && directionalDatability.capabilityId && (
                <StyledRadio style={{ gridColumn: '2/6' }}>
                    <Radio.Group
                        value={item.arrowLabelFrequency}
                        optionType='button'
                        onChange={(e) => {
                            onChange({ ...item, arrowLabelFrequency: e.target.value });
                        }}
                        style={{ display: 'flex' }}
                        size='small'
                        buttonStyle={'solid'}
                    >
                        <Radio style={{ flex: 1, boxShadow: 'none' }} value={undefined}>
                            all
                        </Radio>
                        {[1, 2, 3, 4, 6, 8, 12, 24].map((hourly) => {
                            return (
                                <Radio key={hourly + item.databilityId} style={{ flex: 1, boxShadow: 'none' }} value={hourly}>
                                    {hourly}h
                                </Radio>
                            );
                        })}
                    </Radio.Group>
                </StyledRadio>
            )}
            <div style={{ height: 16 }} />
            <TimeRange
                value={compensatedTimeRange}
                unit='days'
                range='P-31D--P15D'
                onChange={(timeRange) => {
                    const [early, late] = timeRange.split('--').map((d) => Duration.fromISO(d));
                    const decompensatedTimeRange = [early.minus({ days: 1 }), late.plus({ days: 1 })].map((d) => d.toISO()).join('--');
                    onTimeRangeChange({ item, timeRange: decompensatedTimeRange, datability });
                    directionalDatability && onTimeRangeChange({ item, timeRange: decompensatedTimeRange, datability: directionalDatability });
                }}
                mode={THEME_TIME_RANGE.DARK}
            />
            <Text label='Label' value={label} onChange={(label) => onChange({ ...item, label })} />
            <ColourPicker value={colour} onChange={(colour) => onChange({ ...item, colour })} />
            <Slider
                label='Decimal Point (if any)'
                onChange={(dp) => onChange({ ...item, dp })}
                value={item.dp !== undefined ? item.dp : 2}
                min={0}
                max={4}
                marks={generateMarks([0, 1, 2, 3, 4])}
            />
            <Select label='zOrder' value={zOrder} values={times(10, (i) => i)} onChange={(e) => onChange({ ...item, zOrder: e })} />
            <LineStyleFormGroup value={style} onChange={(e) => onChange({ ...item, style: e })} />
            <Select
                label='Smoothing'
                value={smoothing}
                values={smoothingOptions.filter((a) => !a.isDeprecated).map((s) => s.type)}
                onChange={(e) => onChange({ ...item, smoothing: e })}
            />
            <Select label='Symbol' value={symbol} values={symbolOptions} onChange={(e) => onChange({ ...item, symbol: e })} />
            <Slider
                label='Line thickness'
                onChange={(lineThickness) => onChange({ ...item, lineThickness })}
                value={item.lineThickness ?? 1}
                min={1}
                max={10}
                marks={generateMarks(Array.from({ length: 10 }, (v, i) => i + 1))}
            />
            <Divider />
            <ExtremesConfigurator onChange={onChange} item={item} />
            <div style={{ gridColumn: '1 / 6', textAlign: 'right' }}>
                <Button danger icon={<DeleteOutlined />} onClick={onDelete}>
                    Delete
                </Button>
            </div>
        </>
    );
};

export default LineGraphItem;
